import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  X, User, Mail, Phone, MapPin, Calendar, Briefcase, Shield, Building2,
  CreditCard, Landmark, Users, Lock, Eye, EyeOff, Save, AlertCircle, Award,
  Clock, ChevronRight, Star, Trophy, Medal, Crown, Heart, ThumbsUp, Zap, Flame, Target, Gem, Sparkles
} from 'lucide-react';
import TenureBadge, { calculateYearsOfService } from './TenureBadge';
import { formatDate } from '../lib/dateUtils';
import { formatCurrency } from '../lib/formatters';

const BACKEND_URL = '';

// Icon mapping for custom badges
const ICON_MAP = {
  star: Star, trophy: Trophy, medal: Medal, award: Award, crown: Crown,
  heart: Heart, 'thumbs-up': ThumbsUp, zap: Zap, flame: Flame, target: Target,
  gem: Gem, sparkles: Sparkles, shield: Shield, briefcase: Briefcase,
};

/**
 * EmployeeProfileModal
 * Full profile view and edit modal for employees
 */
export default function EmployeeProfileModal({ isOpen, onClose, employee, api, onUpdate }) {
  const [formData, setFormData] = useState({});
  const [showCtc, setShowCtc] = useState(false);
  const [ctcPassword, setCtcPassword] = useState('');
  const [ctcError, setCtcError] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [badges, setBadges] = useState([]);
  
  // Calculate tenure
  const firstWorkingDay = employee?.employment?.first_working_day;
  const tenure = calculateTenure(firstWorkingDay);
  const yearsOfService = calculateYearsOfService(firstWorkingDay);
  
  useEffect(() => {
    if (isOpen && employee) {
      setFormData({
        // Personal - Editable
        first_name: employee.personal?.first_name || employee.personal?.full_name?.split(' ')[0] || '',
        last_name: employee.personal?.last_name || employee.personal?.full_name?.split(' ').slice(1).join(' ') || '',
        // Contact - Editable
        mobile_number: employee.contact?.mobile_number || '',
        address: employee.contact?.address || '',
        // Identity - Editable
        aadhar_number: employee.personal?.aadhar_number || '',
        pan_number: employee.personal?.pan_number || '',
        // Bank - Editable
        account_number: employee.bank_details?.account_number || '',
        bank_name: employee.bank_details?.bank_name || '',
        ifsc_code: employee.bank_details?.ifsc_code || '',
        // Emergency - Editable
        emergency_contact_name: employee.emergency_contact?.name || '',
        emergency_contact_number: employee.emergency_contact?.phone || '',
      });
      setShowCtc(false);
      setCtcPassword('');
      setCtcError('');
      fetchBadges();
    }
  }, [isOpen, employee]);
  
  const fetchBadges = async () => {
    try {
      const response = await api.get('/api/employee/badges');
      setBadges(response.data.badges || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };
  
  const handleUnlockCtc = () => {
    // Simple password check - the actual CTC viewing should be controlled by backend
    // For demo, using a simple check. In production, this would be an API call
    if (ctcPassword === employee?.work_email?.split('@')[0] + '123') {
      setShowCtc(true);
      setCtcError('');
    } else {
      setCtcError('Incorrect password');
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        personal: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          full_name: `${formData.first_name} ${formData.last_name}`.trim(),
          aadhar_number: formData.aadhar_number,
          pan_number: formData.pan_number,
        },
        contact: {
          mobile_number: formData.mobile_number,
          address: formData.address,
        },
        bank_details: {
          account_number: formData.account_number,
          bank_name: formData.bank_name,
          ifsc_code: formData.ifsc_code,
        },
        emergency_contact: {
          name: formData.emergency_contact_name,
          phone: formData.emergency_contact_number,
        },
      };
      
      await api.put('/api/employee/profile', updateData);
      toast.success('Profile updated successfully');
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen || !employee) return null;
  
  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'employment', label: 'Employment', icon: Briefcase },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'bank', label: 'Bank & Identity', icon: CreditCard },
    { id: 'badges', label: 'Badges', icon: Award },
  ];
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" data-testid="employee-profile-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/40 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FFD600] flex items-center justify-center shadow-lg">
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#1a1a2e]">
                {employee.personal?.full_name || 'My Profile'}
              </h2>
              <p className="text-sm text-neutral-500">{employee.employment?.designation || 'Employee'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        
        {/* Tenure Display */}
        <div className="px-6 py-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-xs text-amber-700 font-medium">Tenure</p>
                <p className="text-lg font-bold text-amber-900">{tenure}</p>
              </div>
            </div>
            {yearsOfService >= 1 && (
              <TenureBadge years={yearsOfService} size="small" showLabel={false} animate={false} />
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-white/40 px-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors relative ${
                  isActive ? 'text-yellow-600' : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Personal Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Employee ID</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700 font-mono">
                    {employee._id?.slice(-8).toUpperCase() || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Email</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700">
                    {employee.work_email || 'N/A'}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Gender</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700 capitalize">
                    {employee.personal?.gender || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Date of Birth</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700">
                    {employee.personal?.date_of_birth 
                      ? formatDate(employee.personal.date_of_birth) 
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Employment Tab */}
          {activeTab === 'employment' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Designation</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700">
                    {employee.employment?.designation || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Hierarchy Level</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700 capitalize">
                    {employee.employment?.designation_level || 'N/A'}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Departments</label>
                <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700">
                  {employee.departments?.map(d => d.name || d).join(', ') || 'N/A'}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">First Working Day</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700">
                    {employee.employment?.first_working_day 
                      ? formatDate(employee.employment.first_working_day) 
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Last Working Day</label>
                  <div className="px-3 py-2 bg-neutral-100 rounded-xl text-sm text-neutral-700">
                    {employee.employment?.last_working_day 
                      ? formatDate(employee.employment.last_working_day) 
                      : 'Active Employee'}
                  </div>
                </div>
              </div>
              
              {/* CTC Section with Password Protection */}
              <div className="mt-4 p-4 bg-white/40 rounded-xl border border-white/40">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-neutral-500 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    CTC (Annual)
                  </label>
                </div>
                
                {showCtc ? (
                  <div className="flex items-center gap-2">
                    <div className="px-3 py-2 bg-white rounded-xl text-sm text-neutral-700 font-semibold flex-1">
                      {formatCurrency(employee.employment?.ctc || 0)}
                    </div>
                    <button
                      onClick={() => setShowCtc(false)}
                      className="p-2 text-neutral-500 hover:bg-neutral-200 rounded-xl"
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="password"
                        value={ctcPassword}
                        onChange={(e) => { setCtcPassword(e.target.value); setCtcError(''); }}
                        placeholder="Enter password to view"
                        className="flex-1 px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      />
                      <button
                        onClick={handleUnlockCtc}
                        className="px-3 py-2 bg-neutral-200 hover:bg-gray-300 rounded-xl text-sm font-medium transition-colors flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Unlock
                      </button>
                    </div>
                    {ctcError && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {ctcError}
                      </p>
                    )}
                    <p className="text-xs text-neutral-400">Hint: Your email prefix + &quot;123&quot;</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={formData.mobile_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, mobile_number: e.target.value }))}
                  placeholder="Enter mobile number"
                  className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Address</label>
                <textarea
                  value={formData.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Enter your address"
                  rows={3}
                  className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                />
              </div>
              
              <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-200">
                <h4 className="text-sm font-medium text-red-800 flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" />
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                      placeholder="Emergency contact name"
                      className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Contact Number</label>
                    <input
                      type="tel"
                      value={formData.emergency_contact_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_number: e.target.value }))}
                      placeholder="Emergency contact number"
                      className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Bank & Identity Tab */}
          {activeTab === 'bank' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4" />
                  Identity Documents
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Aadhar Number</label>
                    <input
                      type="text"
                      value={formData.aadhar_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, aadhar_number: e.target.value }))}
                      placeholder="XXXX XXXX XXXX"
                      className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">PAN Number</label>
                    <input
                      type="text"
                      value={formData.pan_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                      placeholder="ABCDE1234F"
                      className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono uppercase"
                    />
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h4 className="text-sm font-medium text-green-800 flex items-center gap-2 mb-3">
                  <Landmark className="w-4 h-4" />
                  Bank Account Details
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Account Number</label>
                    <input
                      type="text"
                      value={formData.account_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                      placeholder="Enter account number"
                      className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bank_name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                        placeholder="Enter bank name"
                        className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 mb-1">IFSC Code</label>
                      <input
                        type="text"
                        value={formData.ifsc_code || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                        placeholder="ABCD0123456"
                        className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono uppercase"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <div className="space-y-4">
              {/* Tenure Badge */}
              {yearsOfService >= 1 && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h4 className="text-sm font-medium text-amber-800 mb-3">Tenure Badge</h4>
                  <div className="flex items-center gap-4">
                    <TenureBadge years={yearsOfService} size="medium" showLabel={true} animate={false} />
                    <div>
                      <p className="text-sm font-medium text-[#1a1a2e]">{yearsOfService} Year{yearsOfService !== 1 ? 's' : ''} of Service</p>
                      <p className="text-xs text-neutral-500">Joined: {formatDate(firstWorkingDay)}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Custom Badges */}
              <div className="p-4 bg-white/40 rounded-xl border border-white/40">
                <h4 className="text-sm font-medium text-[#1a1a2e] mb-3">Recognition Badges</h4>
                {badges.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {badges.map((badge) => {
                      // Handle relative URLs - uses relative paths to stay on same domain
                      let imageUrl = badge.image_url;
                      if (imageUrl) {
                        if (imageUrl.startsWith('/uploads/')) {
                          imageUrl = `/api${imageUrl}`;
                        }
                        // /api/uploads/ and absolute URLs stay as-is
                      }
                      const Icon = ICON_MAP[badge.icon_type] || Award;
                      
                      return (
                        <div key={badge._id || badge.badge_id} className="text-center">
                          {imageUrl ? (
                            <div className="w-10 h-10 rounded-full overflow-hidden mx-auto border-2 shadow-soft" style={{ borderColor: badge.color }}>
                              <img src={imageUrl} alt={badge.name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center mx-auto shadow-soft"
                              style={{ backgroundColor: badge.color }}
                            >
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <p className="text-xs font-medium text-neutral-700 mt-1 truncate max-w-[70px] mx-auto">{badge.name}</p>
                          {badge.count > 1 && (
                            <p className="text-xs text-neutral-500">×{badge.count}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 text-center py-4">No badges earned yet</p>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/40 bg-white/40">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-neutral-700 font-medium rounded-xl hover:bg-neutral-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-yellow-500 text-white font-medium rounded-xl hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Calculate tenure in YY, MM, DD format
 */
function calculateTenure(firstWorkingDay) {
  if (!firstWorkingDay) return 'N/A';
  
  const start = new Date(firstWorkingDay);
  const now = new Date();
  
  if (isNaN(start.getTime())) return 'N/A';
  
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  
  if (days < 0) {
    months--;
    const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += lastMonth.getDate();
  }
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  const parts = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  
  return parts.length > 0 ? parts.join(', ') : 'Just started';
}
